import React, { useMemo, useState } from 'react';
import { RangeInfo } from 'rc-picker/lib/RangePicker';
import { RangeValue } from 'rc-picker/lib/interface';
import moment, { Moment } from 'moment';
import { DatePicker } from 'antd';

import { disableSpecificNumberDates } from 'utils/dateUtils';
import { DateParams } from 'utils/filterUtils';
import { dateRangeDurationInWeeks } from '../utils';

export type DateRangeFilterType = DateParams | undefined;

type Props = {
  pickerKey: string;
  value: DateRangeFilterType;
  onChange: (value: DateRangeFilterType) => void;
  onCalendarChange?: () => void;
};

export default function ChartDateRangePicker(props: Props) {
  const { pickerKey, value, onChange, onCalendarChange } = props;
  const [selectedDate, setSelectedDate] = useState<DateParams | undefined>(
    undefined,
  );

  const onOpenChange = (open: boolean) => {
    if (open) {
      setSelectedDate({});
    } else {
      setSelectedDate(undefined);
    }
  };

  const handleCalendarChange = (
    dates: RangeValue<Moment>,
    formatString: [string, string],
    info: RangeInfo,
  ) => {
    if (dates) {
      setSelectedDate({
        startDate: dates[0],
        endDate: dates[1],
      });
    } else {
      setSelectedDate(undefined);
    }
    onCalendarChange?.();
  };

  const rangePickerValue = useMemo((): RangeValue<Moment> | undefined => {
    if (selectedDate) {
      return Object.entries(selectedDate).length
        ? [
            selectedDate.startDate
              ? moment(selectedDate.startDate, 'MM/DD/YYYY')
              : null,
            selectedDate.endDate
              ? moment(selectedDate.endDate, 'MM/DD/YYYY')
              : null,
          ]
        : undefined;
    }

    return value
      ? [moment(value?.startDate), moment(value?.endDate)]
      : undefined;
  }, [selectedDate, value]);

  const handleSelectDate = (date: RangeValue<Moment>) => {
    if (date) {
      onChange({
        startDate: date[0],
        endDate: date[1],
      });
    } else {
      onChange(undefined);
    }
  };

  return (
    <DatePicker.RangePicker
      key={pickerKey}
      separator="-"
      className="d-flex"
      format="MM/DD/YYYY"
      allowClear={false}
      onOpenChange={onOpenChange}
      onCalendarChange={handleCalendarChange}
      disabledDate={disableSpecificNumberDates(
        selectedDate?.startDate,
        selectedDate?.endDate,
        dateRangeDurationInWeeks,
      )}
      value={rangePickerValue}
      onChange={handleSelectDate}
    />
  );
}
